import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import { MarkdownEditor } from '@shared/components/MarkdownEditor';
import {
  TaskFlowPanel,
  type TaskFlowPanelHandle,
  type TaskFlowSaveState,
} from '@features/Admin/Agent/ui/TaskFlowCanvas';
import {
  useAgentProfileEditControllerAgentProfileEditForUsersMutation,
  type TaskFlowDto,
} from '@shared/services/apiService';
import { getApiErrorMessage } from '@shared/utils/apiError.utils';
import { CopyJobAction } from './CopyJobAction';
import {
  jobMetadata,
  jobSectors,
  type WorkspaceJobPatch,
  type WorkspaceJobProfile,
} from './jobMetadata';

export const AgentJobEditor = ({
  profile,
  workflow,
  canManage,
  loadCurrent,
  onDirtyChange,
  onUpdated,
  onCopied,
  plainInstructions = false,
}: {
  profile: WorkspaceJobProfile;
  workflow: boolean;
  canManage: boolean;
  loadCurrent: () => Promise<WorkspaceJobProfile | undefined>;
  onDirtyChange: (dirty: boolean) => void;
  onUpdated: () => void;
  onCopied: (profile: WorkspaceJobProfile) => void;
  plainInstructions?: boolean;
}) => {
  const [saved, setSaved] = useState(profile);
  const [draft, setDraft] = useState(profile);
  const [flowDirty, setFlowDirty] = useState(false);
  const [flowState, setFlowState] = useState<TaskFlowSaveState>({
    canSave: false,
    isSaving: false,
  });
  const [review, setReview] = useState<'instructions' | 'workflow' | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const flowRef = useRef<TaskFlowPanelHandle>(null);
  const [edit, mutation] = useAgentProfileEditControllerAgentProfileEditForUsersMutation();
  const allowed = canManage && profile.type === 'custom' && saved.type === 'custom';
  const allowedRef = useRef(allowed);
  const active = useRef(true);
  allowedRef.current = allowed;
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      allowedRef.current = false;
    };
  }, []);
  const stale = !isEqual(profile, saved);
  const changed =
    draft.name !== saved.name ||
    draft.prompt !== saved.prompt ||
    draft.firstMessage !== saved.firstMessage ||
    draft.firstMessageInterruptionsEnabled !== saved.firstMessageInterruptionsEnabled ||
    (draft.sector ?? null) !== (saved.sector ?? null) ||
    (draft.summary ?? null) !== (saved.summary ?? null);
  const dirty = changed || flowDirty;
  const saveBusy = useRef(false);
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const save = useCallback(
    async (patch: WorkspaceJobPatch) => {
      if (!allowedRef.current || saveBusy.current)
        throw new Error('This shared job is read only or a save is in progress.');
      saveBusy.current = true;
      setSaving(true);
      setError('');
      setNotice('');
      try {
        const current = await loadCurrent();
        if (!allowedRef.current || current?.type !== 'custom' || !isEqual(current, saved))
          throw new Error('The job or your editing access changed. Reload before saving.');
        const updated = await edit({
          profileId: saved.id,
          agentProfileEditRequestDto: patch,
        }).unwrap();
        if (!active.current) return updated;
        setSaved(updated);
        // A workflow save must not erase unsaved instructions in the adjacent tab.
        setDraft((previous) => ({
          ...updated,
          ...(changed
            ? {
                name: previous.name,
                prompt: previous.prompt,
                firstMessage: previous.firstMessage,
                firstMessageInterruptionsEnabled: previous.firstMessageInterruptionsEnabled,
                ...jobMetadata(previous),
              }
            : {}),
        }));
        setNotice('Saved on server. Every agent using this job receives the update.');
        onUpdated();
        return updated;
      } catch (e) {
        setError(
          getApiErrorMessage(e, e instanceof Error ? e.message : 'Unable to save this job.'),
        );
        throw e;
      } finally {
        saveBusy.current = false;
        if (active.current) setSaving(false);
      }
    },
    [saved, changed, edit, loadCurrent, onUpdated],
  );
  const saveInstructions = async () => {
    setReview(null);
    if (!draft.name.trim() || !draft.prompt?.trim()) {
      setError('A job name and instructions are required.');
      return;
    }
    try {
      const updated = await save({
        name: draft.name.trim(),
        prompt: draft.prompt,
        firstMessage: draft.firstMessage?.trim() || null,
        firstMessageInterruptionsEnabled: draft.firstMessageInterruptionsEnabled,
        ...jobMetadata(draft),
      });
      setDraft(updated);
    } catch {
      /* The visible error retains the draft. */
    }
  };
  const readOnly = !allowed || stale || saving || mutation.isLoading;
  return (
    <Stack gap={2}>
      {stale && (
        <Alert severity="warning">
          This job changed on the server. Your draft is retained; reload or discard it before
          saving.
        </Alert>
      )}
      {!allowed && (
        <Alert severity="info">
          {profile.type === 'system'
            ? 'This system job is managed. Its private instructions cannot be copied or edited here.'
            : profile.type !== 'custom'
              ? 'This job is protected. Create an editable copy before changing instructions or workflow.'
              : 'Agent management permission is required to edit this job.'}
        </Alert>
      )}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, md: 3 },
          display: workflow ? 'none' : 'block',
          '& .rc-md-editor .editor-container': { flexDirection: { xs: 'column', sm: 'row' } },
        }}>
        <Stack gap={2}>
          <TextField
            label="Job name"
            value={draft.name}
            disabled={readOnly}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            inputProps={{ maxLength: 120 }}
          />
          <TextField
            select
            label="Job sector"
            value={draft.sector ?? ''}
            disabled={readOnly}
            onChange={(e) =>
              setDraft({
                ...draft,
                sector: jobSectors.find((sector) => sector.id === e.target.value)?.id ?? null,
              })
            }>
            <MenuItem value="">Unspecified</MenuItem>
            {jobSectors.map((sector) => (
              <MenuItem value={sector.id} key={sector.id}>
                {sector.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Short description"
            value={draft.summary ?? ''}
            disabled={readOnly}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 180 } }}
            helperText={`${(draft.summary || '').length} / 180 · Optional`}
            onChange={(e) => setDraft({ ...draft, summary: e.target.value || null })}
          />
          {plainInstructions ? (
            <TextField
              label="Instructions"
              value={draft.prompt || ''}
              disabled={readOnly}
              onChange={(e) => setDraft({ ...draft, prompt: e.target.value })}
              multiline
              minRows={14}
              fullWidth
            />
          ) : (
            <MarkdownEditor
              label="Instructions"
              value={draft.prompt || ''}
              disabled={readOnly}
              onChange={(prompt) => setDraft({ ...draft, prompt })}
              height={350}
            />
          )}
          <TextField
            label="Opening line"
            value={draft.firstMessage || ''}
            disabled={readOnly}
            multiline
            minRows={2}
            onChange={(e) => setDraft({ ...draft, firstMessage: e.target.value })}
          />
          <FormControlLabel
            label="Allow opening-line interruptions"
            control={
              <Switch
                checked={draft.firstMessageInterruptionsEnabled}
                disabled={readOnly}
                onChange={(_, value) =>
                  setDraft({ ...draft, firstMessageInterruptionsEnabled: value })
                }
              />
            }
          />
          <Button
            variant="contained"
            disabled={readOnly || !changed || !draft.prompt?.trim() || !draft.name.trim()}
            onClick={() => setReview('instructions')}>
            Review and save shared job
          </Button>
        </Stack>
      </Paper>
      <Box sx={{ display: workflow ? 'block' : 'none', minHeight: 400 }}>
        <TaskFlowPanel
          ref={flowRef}
          initialTaskFlow={saved.taskFlow}
          profileName={saved.name}
          readOnly={readOnly}
          onDirtyChange={setFlowDirty}
          onSaveStateChange={setFlowState}
          onSave={async (taskFlow: TaskFlowDto | null) => (await save({ taskFlow })).taskFlow}
        />
        <Button
          sx={{ mt: 2 }}
          variant="contained"
          disabled={readOnly || !flowState.canSave || flowState.isSaving}
          onClick={() => setReview('workflow')}>
          Review and save workflow
        </Button>
      </Box>
      <CopyJobAction
        profile={profile}
        disabled={!canManage || dirty}
        loadCurrent={loadCurrent}
        onCopied={onCopied}
      />
      <Dialog
        open={!!review}
        onClose={() => setReview(null)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="shared-job-review-title">
        <DialogTitle id="shared-job-review-title">Update this shared job?</DialogTitle>
        <DialogContent>
          <Typography>
            Saving updates every agent using “{saved.name}”, including agents in other campaigns.
            Agent voice and provider settings are unchanged.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReview(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={readOnly || flowState.isSaving}
            onClick={() => {
              if (review === 'instructions') void saveInstructions();
              else {
                setReview(null);
                void flowRef.current?.save();
              }
            }}>
            Confirm shared update
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
